/**
 * Embeds: `?token=` only (in-memory); no persistence. No /login redirect.
 * Value may be a tenant JWT from POST /tenants/token-exchange (Bearer) or a legacy API token (Token).
 */

import React, { useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Result, Spin } from 'antd';

import { useAuthStore } from '@/store/authStore';
import { embedBootstrap } from '@/data/auth';
import { useColors } from '@ai-content-hub/hooks';

const TOKEN_QUERY_KEY = 'token';

interface EmbedTokenGateProps {
    children: React.ReactNode;
}

export const EmbedTokenGate: React.FC<EmbedTokenGateProps> = ({ children }) => {
    const colors = useColors();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const tokenParam = searchParams.get(TOKEN_QUERY_KEY);

    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const isLoading = useAuthStore((s) => s.isLoading);
    const authMode = useAuthStore((s) => s.authMode);
    const error = useAuthStore((s) => s.error);

    useEffect(() => {
        if (!tokenParam) {
            return;
        }
        void embedBootstrap(tokenParam);
    }, [tokenParam]);

    useEffect(() => {
        if (!isAuthenticated || authMode !== 'embed_api' || !tokenParam) {
            return;
        }
        const next = new URLSearchParams(location.search);
        next.delete(TOKEN_QUERY_KEY);
        const qs = next.toString();
        navigate(
            {
                pathname: location.pathname,
                search: qs ? `?${qs}` : '',
            },
            { replace: true }
        );
    }, [isAuthenticated, authMode, tokenParam, navigate, location.pathname, location.search]);

    const embedReady = isAuthenticated && authMode === 'embed_api';

    if (tokenParam && isLoading) {
        return (
            <div
                className="embed-token-gate embed-token-gate--loading"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    background: colors.background,
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    if (error && tokenParam) {
        return (
            <div
                className="embed-token-gate embed-token-gate--error"
                style={{ padding: 24, background: colors.background, minHeight: '100vh' }}
            >
                <Result status="error" title="Could not sign in" subTitle={error} />
            </div>
        );
    }

    if (error && !tokenParam) {
        return (
            <div
                className="embed-token-gate embed-token-gate--error"
                style={{ padding: 24, background: colors.background, minHeight: '100vh' }}
            >
                <Result status="warning" title="Session ended" subTitle={error} />
            </div>
        );
    }

    if (!embedReady) {
        return (
            <div
                className="embed-token-gate embed-token-gate--error"
                style={{ padding: 24, background: colors.background, minHeight: '100vh' }}
            >
                <Result
                    status="warning"
                    title="Authentication required"
                    subTitle="Add ?token=… with a tenant JWT (token-exchange) or API token."
                />
            </div>
        );
    }

    return <>{children}</>;
};

export default EmbedTokenGate;
